import React, { useEffect, useMemo, useRef, useState } from "react";
import { t } from "i18next";

import { FIREBASE_COLLECTIONS } from "../../../../utils/Constants/firebaseCollections";
import {
  collection,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { firestore } from "../../../../firebase/firebaseConfig";

import { MessageBubbleCard } from "./MessageBubble";
import { JobSeparator } from "./JobSeparator";
import { LoadEarlierMessagesButton } from "./LoadEarlierMessagesButton";
import { groupMessagesByJob, getThreadMessages, getRecentRepliesByParent, getReplyCountByParent } from "../messageHelpers";
import useTheme from "../../../../hooks/useTheme";
import { useResponsive } from "../../../UI/ResponsiveContext";
import { useDispatch, useSelector } from "react-redux";
import { AppRow, AppColumn } from "../../../UI/Layouts";
import AppText from "../../../UI/Texts/AppTexts";
import { SizedBox } from "../../../UI/Sizes";
import { Icon } from "semantic-ui-react";
import { UserAvatar } from "../../../UI/DisplayImage";
import { MODAL_CANDIDATE_DETAIL } from "../../../Candidate/CandidateDetail/CandidateViewPage";
import { getChatProfile } from "../../chatProfiles";
import { showToast } from "../../../../redux/Snackbar/SnackbarActions";

// Generous on purpose — most relationships never come close to this many
// messages before "load earlier" is needed. Loads the latest page once,
// then only ever appends new messages via an incremental listener (never a
// live top-N query, which would silently drop an older message out of the
// snapshot the moment a new one arrives and make it disappear mid-read).
const MESSAGE_PAGE_SIZE = 100;

export const MessagesList = ({
  currentUser,
  chats,
  selectedChatId,
  chatContainerHeight,
  openModal,
  activeThreadParentId,
  onEnterThread,
  onExitThread,
  isLockedToThread = false,
}) => {
  const { isMobile } = useResponsive();
  const theme = useTheme();
  const dispatch = useDispatch();

  const copyReferenceNumber = (referenceNumber) => {
    if (!referenceNumber || !navigator.clipboard?.writeText) return;
    navigator.clipboard.writeText(referenceNumber);
    dispatch(showToast(t("chat.reference_number_copied"), { type: "success" }));
  };

  const messageContainerRef = useRef(null);
  const threadBottomRef = useRef(null);

  const [messages, setChatMessages] = useState([]);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
  const latestLoadedTimestampRef = useRef(0);
  // Set right before an older page is prepended, so the auto-scroll-to-
  // bottom effect below can skip itself for that one update instead of
  // fighting the scroll-position restoration loadMoreMessages performs.
  const isPrependingOlderRef = useRef(false);

  const { appliedJobs } = useSelector((state) => state.auth);
  const coreConstants = useSelector((state) => state.core.constants) ?? {};

  const currentUserId = currentUser?.uid;

  const currentChatData = chats.find((chat) => selectedChatId === chat.chatId);

  const isAndriod =
    typeof document !== "undefined" &&
    window?.navigator?.userAgent?.toLowerCase()?.includes("android");
  const isSafari =
    typeof document !== "undefined" &&
    /Safari/i.test(window?.navigator?.userAgent) &&
    !/Chrome|CriOS|Edg|OPR|Firefox/i.test(window?.navigator?.userAgent);

  // Get thread data
  const activeParentMessage = activeThreadParentId
    ? messages.find((m) => m.messageId === activeThreadParentId)
    : null;
  const threadMessages = activeThreadParentId
    ? getThreadMessages(messages, activeThreadParentId)
    : [];

  const handleReplyInThread = (parentMessageId) => {
    // Update the frozen lastActiveAt so the preview card loses its highlight
    initialLastActiveAtRef.current = Date.now();
    onEnterThread(parentMessageId);
  };

  const handleExitThread = () => {
    onExitThread();
  };

  const messagesCollectionRef = () => collection(
    firestore,
    FIREBASE_COLLECTIONS.chats,
    selectedChatId,
    FIREBASE_COLLECTIONS.messages,
  );

  // Loads the latest page once (a one-time read, not a live top-N query —
  // see MESSAGE_PAGE_SIZE's comment for why), then only ever appends new
  // arrivals via an incremental listener cursored off the latest message
  // this session has seen. Older history is fetched on demand via
  // loadMoreMessages, never automatically.
  useEffect(() => {
    if (!currentUser || !currentChatData) return;
    let cancelled = false;
    let unsubscribeIncremental = () => {};

    setChatMessages([]);
    setHasMoreMessages(false);
    latestLoadedTimestampRef.current = 0;

    (async () => {
      const initialSnapshot = await getDocs(
        query(
          messagesCollectionRef(),
          orderBy("createdAt", "desc"),
          limit(MESSAGE_PAGE_SIZE),
        ),
      );
      if (cancelled) return;
      const initialPage = initialSnapshot.docs.map((doc) => doc.data()).reverse();
      setChatMessages(initialPage);
      setHasMoreMessages(initialPage.length >= MESSAGE_PAGE_SIZE);
      latestLoadedTimestampRef.current = initialPage.length > 0
        ? initialPage[initialPage.length - 1].createdAt
        : 0;

      unsubscribeIncremental = onSnapshot(
        query(
          messagesCollectionRef(),
          where("createdAt", ">", latestLoadedTimestampRef.current),
          orderBy("createdAt", "asc"),
        ),
        (querySnapshot) => {
          if (querySnapshot.empty) return;
          const incoming = querySnapshot.docs.map((doc) => doc.data());
          setChatMessages((prev) => {
            const knownIds = new Set(prev.map((m) => m.messageId));
            const toAppend = incoming.filter((m) => !knownIds.has(m.messageId));
            if (toAppend.length === 0) return prev;
            return [...prev, ...toAppend];
          });
          const newestIncoming = incoming[incoming.length - 1]?.createdAt;
          if (newestIncoming > latestLoadedTimestampRef.current) {
            latestLoadedTimestampRef.current = newestIncoming;
          }
        },
      );
    })();

    return () => {
      cancelled = true;
      unsubscribeIncremental();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChatData, currentUser, selectedChatId]);

  const loadMoreMessages = async () => {
    if (isLoadingMoreMessages || !hasMoreMessages || messages.length === 0) {
      return;
    }
    setIsLoadingMoreMessages(true);
    const container = messageContainerRef.current;
    const scrollHeightBefore = container?.scrollHeight ?? 0;
    const scrollTopBefore = container?.scrollTop ?? 0;

    try {
      const oldestLoaded = messages[0].createdAt;
      const olderSnapshot = await getDocs(
        query(
          messagesCollectionRef(),
          where("createdAt", "<", oldestLoaded),
          orderBy("createdAt", "desc"),
          limit(MESSAGE_PAGE_SIZE),
        ),
      );
      const olderPage = olderSnapshot.docs.map((doc) => doc.data()).reverse();
      setHasMoreMessages(olderPage.length >= MESSAGE_PAGE_SIZE);
      isPrependingOlderRef.current = true;
      setChatMessages((prev) => [...olderPage, ...prev]);

      // Older content is prepended above the current viewport — without
      // this, the reader's scroll position silently shifts down by
      // whatever height was just inserted, reading as an unrelated jump
      // right as "load earlier" is clicked.
      requestAnimationFrame(() => {
        if (!container) return;
        const scrollHeightAfter = container.scrollHeight;
        container.scrollTop =
          scrollTopBefore + (scrollHeightAfter - scrollHeightBefore);
      });
    } finally {
      setIsLoadingMoreMessages(false);
    }
  };

  // Scroll main view to bottom when messages change (only when not in a
  // thread). The very first load jumps instantly (animating through a
  // whole conversation's history on open would look worse, not better);
  // every message after that scrolls smoothly instead of snapping, which
  // otherwise reads as the screen jumping right as the new bubble appears.
  const hasScrolledOnceRef = useRef(false);
  useEffect(() => {
    hasScrolledOnceRef.current = false;
  }, [selectedChatId]);
  useEffect(() => {
    if (isPrependingOlderRef.current) {
      isPrependingOlderRef.current = false;
      return;
    }
    if (activeThreadParentId || !messageContainerRef.current) return;
    const container = messageContainerRef.current;
    if (!hasScrolledOnceRef.current) {
      container.scrollTop = container.scrollHeight;
      hasScrolledOnceRef.current = true;
    } else {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    }
  }, [messages, activeThreadParentId]);

  // Scroll thread view to bottom when thread messages change
  useEffect(() => {
    if (activeThreadParentId && threadBottomRef.current) {
      threadBottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [activeThreadParentId, threadMessages.length]);

  // Capture lastActiveAt once on mount so it doesn't reset when updateOnlineStatus fires
  const initialLastActiveAtRef = useRef(null);
  const liveLastActiveAt =
    getChatProfile(currentChatData, currentUserId)?.lastActiveAt || 0;
  if (initialLastActiveAtRef.current === null && liveLastActiveAt > 0) {
    initialLastActiveAtRef.current = liveLastActiveAt;
  }
  const userLastActiveAt = initialLastActiveAtRef.current || 0;

  // Group messages by job (memoized)
  const groupedData = useMemo(() => groupMessagesByJob(messages), [messages]);

  // Build maps for inline thread replies (memoized)
  const recentRepliesMap = useMemo(() => getRecentRepliesByParent(messages, 3), [messages]);
  const replyCountMap = useMemo(() => getReplyCountByParent(messages), [messages]);

  // Enrich groups with job details from appliedJobs
  if (groupedData.grouped) {
    groupedData.items.forEach((group) => {
      if (!group.jobId) return;

      const matchingApp = appliedJobs?.find(
        (job) =>
          job.jobDetails?.jobId === group.jobId ||
          job.jobDetails?.shareableJobId === group.jobId ||
          job.jobId === group.jobId
      );
      const jd = matchingApp?.jobDetails;

      if (jd) {
        if (!group.healthcareWorkplaceName) {
          group.healthcareWorkplaceName =
            jd.healthcareWorkplace?.name ||
            jd.healthcareWorkplaceDetails?.name ||
            null;
        }
        if (!group.referenceNumber && jd.referenceNumber) {
          group.referenceNumber = jd.referenceNumber;
        }
      }

      const chatJobData = currentChatData?.metaData?.currentChatJobData;
      if (chatJobData && chatJobData.jobId === group.jobId) {
        if (!group.healthcareWorkplaceName && chatJobData.healthcareWorkplaceName) {
          group.healthcareWorkplaceName = chatJobData.healthcareWorkplaceName;
        }
        if (!group.referenceNumber && chatJobData.referenceNumber) {
          group.referenceNumber = chatJobData.referenceNumber;
        }
      }
    });
  }

  // Sort groups by latest activity (oldest first — normal scroll puts newest at bottom)
  if (groupedData.grouped) {
    const getLatestActivity = (group) => {
      let latest = group.timestamp || 0;
      for (const msg of group.messages) {
        if ((msg.createdAt || 0) > latest) latest = msg.createdAt;
        // Check replies for this message
        const replies = recentRepliesMap[msg.messageId];
        if (replies && replies.length > 0) {
          const lastReply = replies[replies.length - 1];
          if ((lastReply.createdAt || 0) > latest) latest = lastReply.createdAt;
        }
      }
      return latest;
    };
    groupedData.items.sort((a, b) => getLatestActivity(a) - getLatestActivity(b));
  }

  // Compute per-group reply data
  const getGroupReplyData = (group) => {
    let allReplies = [];
    let totalCount = 0;
    let unreadCount = 0;

    for (const message of group.messages) {
      const replies = recentRepliesMap[message.messageId] || [];
      const count = replyCountMap[message.messageId] || 0;
      allReplies = allReplies.concat(replies);
      totalCount += count;
    }

    // Sort all replies by time, take latest 3
    allReplies.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    const recentReplies = allReplies.slice(-3);

    // Count unread
    for (const reply of allReplies) {
      if ((reply.createdAt || 0) > userLastActiveAt) {
        unreadCount++;
      }
    }

    // Find the parent message for entering thread mode
    const parentMessageId = group.messages.find((m) =>
      replyCountMap[m.messageId] > 0 || m.messagesCount > 0
    )?.messageId || group.messages[0]?.messageId;

    return { recentReplies, totalCount, unreadCount, parentMessageId };
  };

  // Resolve the matching application and job details for the thread
  const threadCandidateData = useMemo(() => {
    if (!activeThreadParentId) return null;
    return appliedJobs?.find(
      (job) =>
        job.jobDetails?.jobId === activeThreadParentId ||
        job.jobId === activeThreadParentId,
    ) || null;
  }, [activeThreadParentId, appliedJobs]);

  const threadJobDetails = useMemo(() => {
    if (!activeThreadParentId) return null;
    if (threadCandidateData?.jobDetails) return threadCandidateData.jobDetails;
    const parentMsg = activeParentMessage;
    if (parentMsg?.contentData?.metaData?.chatJobData) {
      return parentMsg.contentData.metaData.chatJobData;
    }
    // The root may predate the backend attaching chatJobData to every
    // message in a thread — fall back to a reply before falling all the
    // way back to the chat's single "most recently active job" tracker,
    // which would show the wrong job if a different thread is now newer.
    const replyWithJobData = threadMessages.find(
      (reply) => reply?.contentData?.metaData?.chatJobData,
    );
    if (replyWithJobData) {
      return replyWithJobData.contentData.metaData.chatJobData;
    }
    return currentChatData?.metaData?.currentChatJobData || null;
  }, [activeThreadParentId, threadCandidateData, activeParentMessage, threadMessages, currentChatData]);

  // Resolve other user profile for the thread header. Anchor on this
  // application's contact person — the chat may carry many recruiters
  // across applications to the same workplace, and picking "first non-self"
  // surfaces whichever recruiter happens to be ordered first.
  const otherUserProfile = (() => {
    if (!currentChatData?.profilesData || !currentUserId) return null;
    const profiles = currentChatData.profilesData;
    const isVisible = (p) =>
      p && p.userId !== currentUserId && p.active !== false && p.name !== "Deleted User";

    const contactUid = threadCandidateData?.contactPerson?.uid;
    if (contactUid && profiles[contactUid] && isVisible(profiles[contactUid])) {
      return profiles[contactUid];
    }

    const visibleProfiles = Object.values(profiles).filter(isVisible);
    const recruiter = visibleProfiles.find((p) => p.role === "recruiter");
    return recruiter || visibleProfiles[0] || null;
  })();

  // Thread View (render function, not a component — avoids remount on parent re-render)
  const renderThreadView = () => (
    <AppColumn style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
      {/* Thread Header */}
      <AppRow
        crossAxisAlignment="center"
        style={{
          padding: theme.spacing.elementPadding,
          borderBottom: `1px solid ${theme.colors.lightGrey}`,
          flexShrink: 0,
          gap: "0.5rem",
        }}
      >
        {!isLockedToThread && (
          <div
            onClick={handleExitThread}
            style={{
              cursor: "pointer",
              padding: "4px",
              borderRadius: "4px",
            }}
          >
            <Icon name="arrow left" color={theme.primaryColor} />
          </div>
        )}
        {otherUserProfile && (
          <UserAvatar
            url={otherUserProfile.profileImageUrl}
            name={otherUserProfile.name}
            style={{ height: "32px", width: "32px" }}
          />
        )}
        {threadJobDetails ? (
          <AppColumn style={{ flex: 1 }}>
            <AppText.Caption fontWeight={600}>
              {[
                threadJobDetails.profession,
                threadJobDetails.healthcareWorkplace?.name ||
                  threadJobDetails.healthcareWorkplaceName ||
                  threadJobDetails.workplaceName,
              ]
                .filter(Boolean)
                .join(" — ")}
            </AppText.Caption>
            {threadJobDetails.referenceNumber && (
              <AppText.FootnoteSmall
                color="grey"
                cursor="pointer"
                onClick={() => copyReferenceNumber(threadJobDetails.referenceNumber)}
              >
                #{threadJobDetails.referenceNumber}{" "}
                <Icon name="copy outline" style={{ fontSize: "0.85em" }} />
              </AppText.FootnoteSmall>
            )}
          </AppColumn>
        ) : (
          <AppText.SubHeading fontWeight={600}>{t("chat.thread")}</AppText.SubHeading>
        )}
        {threadCandidateData && (
          <AppText.FootnoteSmall
            onClick={() => {
              openModal(MODAL_CANDIDATE_DETAIL, {
                candidateData: threadCandidateData,
                jobId: threadCandidateData.jobDetails?.jobId ?? threadCandidateData.jobId,
              });
            }}
            color={theme.primaryColor}
            cursor="pointer"
            textDecoration="underline"
            style={{
              marginLeft: "auto",
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            {t("view_application")}
          </AppText.FootnoteSmall>
        )}
      </AppRow>

      {/* Thread Content */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: theme.spacing.contentPadding,
        }}
      >
        {hasMoreMessages && (
          <LoadEarlierMessagesButton
            isLoading={isLoadingMoreMessages}
            onLoadMore={loadMoreMessages}
          />
        )}

        {/* Parent Message */}
        {activeParentMessage && (
          <div
            style={{
              padding: theme.spacing.elementPadding,
              backgroundColor: theme.colors.lightGrey,
              borderRadius: theme.radius.default,
              marginBottom: theme.spacing.elementPadding,
            }}
          >
            <MessageBubbleCard
              messageData={activeParentMessage}
              currentUserId={currentUserId}
              profilesData={currentChatData?.profilesData}
              showReplyInThread={false}
            />
          </div>
        )}

        {/* Reply Divider */}
        <AppRow
          style={{
            margin: `${theme.spacing.elementPadding}px 0`,
          }}
        >
          <div style={{ flex: 1, height: "1px", backgroundColor: theme.colors.lightGrey }} />
          <AppText.FootnoteSmall
            style={{ padding: `0 ${theme.spacing.elementPadding}px` }}
            color="#888"
          >
            {t(threadMessages.length === 1 ? "chat.reply" : "chat.replies", { count: threadMessages.length })}
          </AppText.FootnoteSmall>
          <div style={{ flex: 1, height: "1px", backgroundColor: theme.colors.lightGrey }} />
        </AppRow>

        {/* Thread Replies */}
        {threadMessages.map((message) => (
          <MessageBubbleCard
            key={message.messageId || message.id}
            messageData={message}
            currentUserId={currentUserId}
            profilesData={currentChatData?.profilesData}
            showReplyInThread={false}
          />
        ))}
        <div ref={threadBottomRef} />
      </div>
    </AppColumn>
  );

  // Main Messages View (render function, not a component — avoids remount on parent re-render)
  const renderMainMessagesView = (containerRef, paddingStyle) => (
    <div
      ref={containerRef}
      style={{
        overflowY: "auto",
        scrollbarWidth: "none",
        overflowX: "hidden",
        msOverflowStyle: "none",
        ...paddingStyle,
      }}
    >
      {hasMoreMessages && (
        <LoadEarlierMessagesButton
          isLoading={isLoadingMoreMessages}
          onLoadMore={loadMoreMessages}
        />
      )}
      {groupedData.grouped
        ? groupedData.items.map((group, groupIndex) => {
            if (!group.jobId) {
              // Non-job messages: render plain without job card or thread actions
              return group.messages.map((message) => (
                <MessageBubbleCard
                  key={message.messageId || message.id}
                  messageData={message}
                  currentUserId={currentUserId}
                  profilesData={currentChatData?.profilesData}
                  showReplyInThread={false}
                />
              ));
            }
            const { recentReplies, totalCount, unreadCount, parentMessageId } = getGroupReplyData(group);
            return (
              <JobSeparator
                key={group.jobId || `group-${groupIndex}`}
                profession={group.profession}
                timestamp={group.timestamp}
                jobId={group.jobId}
                appliedJobs={appliedJobs}
                openModal={openModal}
                coreConstants={coreConstants}
                healthcareWorkplaceName={group.healthcareWorkplaceName}
                referenceNumber={group.referenceNumber}
                accentIndex={groupIndex}
                recentReplies={recentReplies}
                totalReplyCount={totalCount}
                unreadCount={unreadCount}
                profilesData={currentChatData?.profilesData}
                onReplyTap={() => handleReplyInThread(parentMessageId)}
                onViewAllReplies={() => handleReplyInThread(parentMessageId)}
              >
                {group.messages.map((message) => (
                  <MessageBubbleCard
                    key={message.messageId || message.id}
                    messageData={message}
                    currentUserId={currentUserId}
                    profilesData={currentChatData?.profilesData}
                    showReplyInThread={false}
                  />
                ))}
              </JobSeparator>
            );
          })
        : groupedData.items.map((message) => (
            <MessageBubbleCard
              key={message.messageId || message.id}
              messageData={message}
              currentUserId={currentUserId}
              profilesData={currentChatData?.profilesData}
              showReplyInThread={false}
            />
          ))}
    </div>
  );

  if (isMobile) {
    if (activeThreadParentId) {
      return (
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {renderThreadView()}
        </div>
      );
    }

    return (
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          justifyContent: "flex-end",
          alignContent: "flex-end",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {renderMainMessagesView(messageContainerRef, {
            paddingLeft: theme.spacing.contentPadding,
            paddingRight: isMobile && theme.spacing.contentPadding,
            paddingTop: theme.spacing.contentPadding,
            paddingBottom: (() => {
              if (isSafari || isAndriod) return "20vh";
              return "10vh";
            })(),
          })}
      </div>
    );
  } else {
    if (activeThreadParentId) {
      return (
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {renderThreadView()}
        </div>
      );
    }

    return renderMainMessagesView(messageContainerRef, {
        flex: 1,
        alignContent: "flex-end",
        paddingLeft: theme.spacing.elementPadding,
        paddingRight: theme.spacing.elementPadding,
        paddingTop: theme.spacing.contentPadding,
        paddingBottom: theme.spacing.elementPadding,
      });
  }
};
